import React from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { useT, fmt } from '../i18n/I18nContext';

// Shared footer used on both the landing page (after FinalCTASection) and the
// /example & /get-started pages. Single source of truth for footer nav + copyright year.
export default function Footer() {
  const t = useT();

  const navItems: { label: string; to: string }[] = [
    { label: t.footer.links.howItWorks, to: '/#how-it-works' },
    { label: t.footer.links.example,    to: '/example'       },
    { label: t.footer.links.whyBetter,  to: '/#why-better'   },
    { label: t.footer.links.faq,        to: '/#faq'          },
  ];

  return (
    <footer style={{
      borderTop: '1px solid rgba(255,255,255,0.12)',
      padding: 'clamp(36px, 4vw, 56px) clamp(24px, 5vw, 80px) clamp(20px, 2.5vw, 28px)',
      position: 'relative',
      zIndex: 2,
    }}>
      <div
        className="sb-footer-grid"
        style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr 1fr',
          gap: 'clamp(24px, 4vw, 56px)',
        }}
      >

        {/* Brand column */}
        <div>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
            <Logo size={28} />
            <span style={{ fontWeight: 800, fontSize: 17, color: 'rgba(255,255,255,0.92)', letterSpacing: '-0.01em' }}>
              SkinBloom
            </span>
          </Link>
          <p style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.50)',
            lineHeight: 1.65,
            marginTop: 14,
            maxWidth: 320,
          }}>
            {t.footer.tagline}
          </p>
        </div>

        {/* Navigation */}
        <FooterColumn label={t.footer.nav}>
          {navItems.map(item => (
            <FooterLink key={item.label} to={item.to}>{item.label}</FooterLink>
          ))}
        </FooterColumn>

        {/* Contact / legal */}
        <FooterColumn label={t.footer.contact}>
          <FooterAnchor href="mailto:devblueprint.tech@gmail.com">devblueprint.tech@gmail.com</FooterAnchor>
          <FooterLink to="/#trust">{t.footer.privacy}</FooterLink>
        </FooterColumn>
      </div>

      {/* Bottom bar */}
      <div style={{
        maxWidth: 1100, margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 'clamp(28px, 3.5vw, 44px)',
        paddingTop: 'clamp(16px, 2vw, 22px)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.40)', fontWeight: 400 }}>
          {fmt(t.footer.copyright, { year: new Date().getFullYear() })}
        </p>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.32)', fontWeight: 400 }}>
          {t.footer.disclaimer}
        </p>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .sb-footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </footer>
  );
}

function FooterColumn({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{
        fontSize: 11, fontWeight: 700,
        color: 'rgba(255,255,255,0.45)',
        letterSpacing: '0.10em', textTransform: 'uppercase',
        marginBottom: 14,
      }}>{label}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>{children}</div>
    </div>
  );
}

const linkStyle: React.CSSProperties = {
  fontSize: 13.5,
  color: 'rgba(255,255,255,0.74)',
  textDecoration: 'none',
  width: 'fit-content',
  maxWidth: '100%',
  wordBreak: 'break-word',
  transition: 'color 0.18s ease',
};
const linkHoverIn  = (e: React.MouseEvent<HTMLElement>) => { (e.currentTarget as HTMLElement).style.color = 'white'; };
const linkHoverOut = (e: React.MouseEvent<HTMLElement>) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.74)'; };

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} style={linkStyle} onMouseEnter={linkHoverIn} onMouseLeave={linkHoverOut}>
      {children}
    </Link>
  );
}
function FooterAnchor({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} style={linkStyle} onMouseEnter={linkHoverIn} onMouseLeave={linkHoverOut}>
      {children}
    </a>
  );
}
